import type {
  CreateSimulationIssueInput,
  PlaytestCatalogItem,
  PlaytestExperienceRating,
  PlaytestView,
  SavePlaytestExperienceReviewInput,
  SimulationIssueSeverity,
  SimulationIssueType,
} from "@actale/contracts";
import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { api, ApiError, storyEntryMediaUrl } from "./api.js";
import { storyPreludeNarrative } from "./story-prelude-narrative.js";
import { playtestEntryFailureText, playtestEntryQueryText, playtestEntryWasRejected } from "./playtest-entry.js";

const ISSUE_TAGS: Array<{
  type: SimulationIssueType;
  label: string;
  hint: string;
}> = [
  { type: "DIALOGUE_QUALITY", label: "对话生硬", hint: "台词不像真人，或不符合角色性格" },
  { type: "PACING_ERROR", label: "节奏问题", hint: "推进太快、太慢或缺少停顿" },
  { type: "VOICE_MISMATCH", label: "音色不合", hint: "声音、语速或情绪不贴合角色" },
  { type: "SECRET_LEAK", label: "秘密泄露", hint: "角色知道了不该知道的信息" },
  { type: "CHARACTER_CROSSTALK", label: "角色串台", hint: "角色说了属于另一个人的内容" },
  { type: "USER_ACTION_TAKEN", label: "替我行动", hint: "AI 擅自决定了玩家的行为" },
  { type: "IMMERSION_BREAK", label: "出戏", hint: "表达或交互破坏了沉浸感" },
  { type: "TIME_CONTRADICTION", label: "时间矛盾", hint: "事件先后或时间表述互相冲突" },
  { type: "LOCATION_CONTRADICTION", label: "地点矛盾", hint: "地点层级或场所描述互相冲突" },
  { type: "CHARACTER_PRESENCE_ERROR", label: "人物位置错误", hint: "人物没有合理移动却出现在另一地点" },
  { type: "KNOWLEDGE_ORDER_ERROR", label: "知情顺序错误", hint: "角色提前知道了尚未获得的信息" },
  { type: "PROP_PROVENANCE_MISSING", label: "物件来源缺失", hint: "关键物件突然出现或归属不清" },
  { type: "CAUSAL_GAP", label: "因果断裂", hint: "事件结果缺少前置原因" },
  { type: "POV_ERROR", label: "视角错误", hint: "玩家资料混用第一、第二或第三人称" },
  { type: "CANONICAL_TRUTH_MISSING", label: "唯一真相冲突", hint: "故事出现互不相容的真相版本" },
  { type: "ENDING_UNSUPPORTED", label: "结局依据不足", hint: "结局缺少事实、证据或角色选择支撑" },
  { type: "OTHER", label: "其他", hint: "记录其他体验问题" },
];

const SEVERITY_LABELS: Record<SimulationIssueSeverity, string> = {
  ERROR: "严重",
  WARNING: "需要优化",
  NOTE: "观察记录",
};

type ExperienceReviewKey = Exclude<keyof SavePlaytestExperienceReviewInput, "note">;

const EXPERIENCE_REVIEW_ITEMS: Array<{
  key: ExperienceReviewKey;
  label: string;
  question: string;
}> = [
  { key: "openingClarity", label: "开场清楚", question: "第一次接触故事时，是否立刻看懂眼前发生了什么？" },
  { key: "briefContinuity", label: "衔接连续", question: "序幕、选角、私人简报和聊天室是否像同一件事连续发生？" },
  { key: "directResponse", label: "直接回应", question: "角色是否真正回答或承接了你的话？" },
  { key: "characterBelievability", label: "角色像真人", question: "角色是否有自己的态度，而不是播报情况或复述安排？" },
  { key: "pacingNatural", label: "节奏自然", question: "事件是否留出理解和选择时间，没有突然跳步？" },
  { key: "playerAgency", label: "行动有效", question: "你的决定是否真实改变场景、状态和其他角色的反应？" },
  { key: "endingCausality", label: "结局因果", question: "如果已经到结局，是否清楚为什么得到这个结果？" },
  { key: "wantsToContinue", label: "愿意继续", question: "只凭普通玩家的感觉，你是否还想继续玩下去？" },
];

const EXPERIENCE_RATING_LABELS: Record<PlaytestExperienceRating, string> = {
  PASS: "通过",
  FAIL: "不通过",
  NOT_ASSESSED: "未评审",
};

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

function elapsedLabel(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

export function playtestEntryVideoUrl(videoKey: string, extension: "mp4" | "jpg"): string {
  return /^(?:entry|prelude|upload)-/u.test(videoKey)
    ? storyEntryMediaUrl(videoKey, extension)
    : `/story-entry-videos/${videoKey}.${extension === "jpg" ? "png" : extension}`;
}

function CharacterPortrait({
  name,
  portraitKey,
  className,
}: {
  name: string;
  portraitKey: string;
  className: string;
}): React.ReactNode {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [portraitKey]);

  return (
    <span className={className} aria-hidden="true">
      {portraitKey && !failed ? (
        <img
          src={`/character-portraits/${portraitKey}.jpg`}
          alt=""
          onError={() => setFailed(true)}
        />
      ) : (
        <strong>{name.slice(0, 1)}</strong>
      )}
    </span>
  );
}

type PortraitCharacter = Pick<PlaytestCatalogItem["cast"][number], "id" | "name" | "portraitKey">;
type PortraitMessage = Pick<PlaytestView["messages"][number], "senderType" | "characterId" | "senderName">;

interface PendingPlaytestReply {
  content: string;
  clientMessageId: string;
  status: "SENDING" | "FAILED";
}

export function playtestEnterSubmitsMessage(input: {
  key: string;
  shiftKey: boolean;
  isComposing: boolean;
}): boolean {
  return input.key === "Enter" && !input.shiftKey && !input.isComposing;
}

export function playtestCanSubmitMessage(input: {
  content: string;
  canSend: boolean;
  busy: boolean;
  hasPendingReply: boolean;
}): boolean {
  return Boolean(input.content.trim()) && input.canSend && !input.busy && !input.hasPendingReply;
}

export function playtestHasClientMessage(
  messages: Array<Pick<PlaytestView["messages"][number], "clientMessageId">>,
  clientMessageId: string,
): boolean {
  return messages.some((message) => message.clientMessageId === clientMessageId);
}

export function playtestMessagePortraitKey(
  playableCharacter: PortraitCharacter,
  otherCharacters: PortraitCharacter[],
  message: PortraitMessage,
): string {
  if (message.senderType === "USER") return playableCharacter.portraitKey;
  return [playableCharacter, ...otherCharacters].find((character) =>
    (message.characterId && character.id === message.characterId)
    || character.name === message.senderName
  )?.portraitKey ?? "";
}

function castStatusLabel(member: PlaytestCatalogItem["cast"][number]): string {
  if (member.playable) return "可扮演";
  if (member.kind === "NARRATIVE") return "不可扮演 · 剧情人物";
  if (member.kind === "BACKGROUND") return "不可扮演 · 背景人物";
  return "不可扮演 · AI 演绎";
}

function RoleSetup({
  catalog,
  stage,
  setStage,
  selectedStoryId,
  selectedRoleId,
  busy,
  onStory,
  onRole,
  onStart,
  roleModelProfile,
  onRoleModelProfile,
}: {
  catalog: PlaytestCatalogItem[];
  stage: "prelude" | "roles";
  setStage: (stage: "prelude" | "roles") => void;
  selectedStoryId: string;
  selectedRoleId: string;
  busy: boolean;
  onStory: (id: string) => void;
  onRole: (id: string) => void;
  onStart: () => void;
  roleModelProfile: "CONFIGURED" | "DOUBAO_CHARACTER";
  onRoleModelProfile: (profile: "CONFIGURED" | "DOUBAO_CHARACTER") => void;
}): React.ReactNode {
  const story = catalog.find((item) => item.id === selectedStoryId) ?? null;
  const selectedRole = story?.playableRoles.find((role) => role.id === selectedRoleId) ?? null;
  const entryVideoBeat = story?.publicPrelude.find((beat) => beat.videoKey?.trim()) ?? null;

  return (
    <div className="playtest-entry-shell">
      <header className="playtest-entry-toolbar">
        <div>
          <span className="playtest-mode-badge">PLAYER VIEW</span>
          <div><strong>内部试玩入口</strong><small>按照真实玩家的进入顺序体验当前草稿</small></div>
        </div>
        <label>
          <span>故事线草稿</span>
          <select value={selectedStoryId} onChange={(event) => onStory(event.target.value)} disabled={busy}>
            <option value="">请选择故事线</option>
            {catalog.map((item) => (
              <option value={item.id} key={item.id}>
                {item.title} · R{item.draftRevision}{item.ready ? "" : ` · ${item.blockingIssueCount} 个阻止项`}
              </option>
            ))}
          </select>
        </label>
      </header>

      {!story ? (
        <section className="playtest-entry-empty">
          <span>01</span><h1>先选择一条故事线</h1><p>系统会先展示公开前情，再进入角色选择。</p>
        </section>
      ) : stage === "prelude" ? (
        <section className="playtest-prelude-stage">
          <header className="playtest-entry-heading">
            <div><span>01 · 故事前情</span><h1>进入《{story.title}》</h1><p>{story.summary}</p></div>
            <dl>
              <div><dt>草稿</dt><dd>R{story.draftRevision}</dd></div>
              <div><dt>时长</dt><dd>约 {story.targetDurationMinutes} 分钟</dd></div>
              <div><dt>故事人物</dt><dd>{story.cast.length} 位 · {story.playableRoles.length} 位可扮演</dd></div>
            </dl>
          </header>
          {entryVideoBeat ? (
            <div className="playtest-entry-video">
              <video
                src={playtestEntryVideoUrl(entryVideoBeat.videoKey!, "mp4")}
                poster={playtestEntryVideoUrl(entryVideoBeat.videoKey!, "jpg")}
                autoPlay
                controls
                playsInline
                onEnded={() => setStage("roles")}
              />
              <div><small>故事序幕</small><h2>{entryVideoBeat.title}</h2><p>{entryVideoBeat.body}</p></div>
            </div>
          ) : <div className="playtest-prelude-timeline">
            {(story.publicPrelude.length ? story.publicPrelude : [{
              key: "story-summary",
              participantCharacterKeys: [],
              title: "故事从这里开始",
              body: story.summary || "这条故事线尚未补充公开前情。",
            }]).map((beat, index) => (
              <article className="playtest-prelude-beat" key={beat.key}>
                <header><span>{String(index + 1).padStart(2, "0")}</span><small>{beat.title}</small></header>
                <div className={`playtest-prelude-artwork artwork-${beat.artworkKey || "default"}`} aria-hidden="true"><i /><i /></div>
                <h2>{beat.title}</h2>
                <p>{storyPreludeNarrative(beat, { index, previousBeat: index > 0 ? story.publicPrelude[index - 1] : undefined })}</p>
              </article>
            ))}
          </div>}
          <footer className="playtest-entry-transition">
            <button type="button" className="primary-button" onClick={() => setStage("roles")} disabled={busy || !story.ready || !story.playableRoles.length}>
              {story.ready ? (entryVideoBeat ? "跳过视频，选择角色" : "进入今晚，选择角色") : `还有 ${story.blockingIssueCount} 个阻止项`}
            </button>
          </footer>
        </section>
      ) : (
        <section className="playtest-role-stage">
          <button type="button" className="playtest-entry-back" onClick={() => setStage("prelude")} disabled={busy}>← 返回故事前情</button>
          <header className="playtest-entry-heading compact">
            <div><span>02 · 选择角色</span><h1>今晚，你想成为谁？</h1></div>
            <dl>
              <div><dt>故事人物</dt><dd>{story.cast.length} 位</dd></div>
              <div><dt>可扮演</dt><dd>{story.playableRoles.length} 位</dd></div>
              <div><dt>预计体验</dt><dd>约 {story.targetDurationMinutes} 分钟</dd></div>
            </dl>
          </header>
          <div className="playtest-role-grid">
            {story.cast.map((member) => {
              const role = member.playable
                ? story.playableRoles.find((candidate) => candidate.id === member.id) ?? null
                : null;
              const active = role?.id === selectedRoleId;
              const content = (
                <>
                  <CharacterPortrait name={member.name} portraitKey={member.portraitKey} className="playtest-role-portrait" />
                  <span className="playtest-role-copy">
                    <span>
                      <strong>{member.name}</strong>
                      {role ? <i className="playtest-role-radio">{active ? "✓" : ""}</i> : <i className="playtest-cast-status">{castStatusLabel(member)}</i>}
                    </span>
                    <small>{member.publicDescription || "这名人物的公开介绍尚未配置。"}</small>
                    <em>{role ? "为什么来到这里" : "如何参与故事"}</em>
                    <p>{member.publicEntryReason || "这名人物与当前事件的公开关系尚未配置。"}</p>
                    {active ? <b>已选中 · 确认后揭晓私人记忆</b> : null}
                  </span>
                </>
              );
              return role ? (
                <button type="button" className={`playtest-role-choice ${active ? "active" : ""}`} onClick={() => onRole(role.id)} disabled={busy} key={member.id}>
                  {content}
                </button>
              ) : (
                <article className="playtest-role-choice read-only" key={member.id} aria-label={`${member.name}，${castStatusLabel(member)}`}>
                  {content}
                </article>
              );
            })}
          </div>
          <footer className="playtest-role-actions">
            <div><span>隐私边界</span><p>选角前不显示私人记忆、个人目标、秘密或导演决策。</p>
              <label><span>角色演绎模型</span><select aria-label="角色演绎模型" value={roleModelProfile} disabled={busy} onChange={(event) => onRoleModelProfile(event.target.value as "CONFIGURED" | "DOUBAO_CHARACTER")}>
                <option value="CONFIGURED">GPT 管剧情，角色也由 GPT 扮演</option>
                {story.slug === "mist-over-lighthouse" ? <option value="DOUBAO_CHARACTER">GPT 继续管剧情，角色换成豆包来扮演</option> : null}
              </select></label>
              {roleModelProfile === "DOUBAO_CHARACTER" ? <>
                <p>仅本局角色演绎使用豆包；全局导演仍使用当前配置。新演绎版开场也会调用模型，每次只提交一次，失败不自动重试，不计入正式发布验收。</p>
              </> : <strong>智能体版 · 全局导演 + 角色演绎</strong>}
            </div>
            <button type="button" className="primary-button playtest-enter" onClick={onStart} disabled={busy || !story.ready || !selectedRole}>
              {busy ? "正在布置故事…" : selectedRole ? `以 ${selectedRole.name} 身份进入故事` : "请选择一个角色"}
            </button>
          </footer>
        </section>
      )}
    </div>
  );
}

function MessageAudioButton({
  runId,
  message,
  label,
}: {
  runId: string;
  message: PlaytestView["messages"][number];
  label?: string;
}): React.ReactNode {
  const [state, setState] = useState<"idle" | "loading" | "playing">("idle");
  const [notice, setNotice] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef("");

  useEffect(() => () => {
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  async function play(): Promise<void> {
    if (!message.canPlay) {
      setNotice("这句悄悄话不是说给你的，你只能看到它发生过。");
      return;
    }
    if (state === "playing") {
      audioRef.current?.pause();
      setState("idle");
      return;
    }
    setNotice("");
    setState("loading");
    try {
      if (!urlRef.current) {
        const blob = await api.playtestMessageAudio(runId, message.id);
        urlRef.current = URL.createObjectURL(blob);
      }
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.src = urlRef.current;
      audio.onended = () => setState("idle");
      audio.onerror = () => {
        setState("idle");
        setNotice("语音暂时无法播放，请继续使用文字体验。");
      };
      await audio.play();
      setState("playing");
    } catch (caught) {
      setState("idle");
      setNotice(errorText(caught, "语音暂时无法播放，请继续使用文字体验。"));
    }
  }

  const whisper = message.visibility === "WHISPER";
  return (
    <div className={whisper ? "playtest-whisper-audio" : "playtest-public-audio"}>
      <button type="button" className={`${message.canPlay ? "playable" : "locked"} ${state}`} onClick={() => void play()}>
        <span aria-hidden="true">{state === "loading" ? "…" : state === "playing" ? "■" : "▶"}</span>
        <span className="playtest-audio-wave" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></span>
        <strong>{state === "loading" ? "正在生成音色" : whisper ? `${message.durationSeconds ?? 1}″` : state === "playing" ? "正在播放" : (label ?? "听角色说")}</strong>
        {!message.canPlay ? <span aria-hidden="true">⌁</span> : null}
      </button>
      {notice ? <small role="status">{notice}</small> : null}
    </div>
  );
}

function soundEventCopy(message: PlaytestView["messages"][number]): {
  title: string;
  subtitle: string;
  action: string;
} {
  const context = message.contextLabel?.trim();
  if (context === "电话中") {
    return {
      title: "失踪来电接入",
      subtitle: message.senderName === "失真来电"
        ? "电话中传来无法确认身份的失真声音"
        : `电话中 · ${message.senderName}`,
      action: "播放来电录音",
    };
  }
  if (context === "录音中") return { title: "录音开始播放", subtitle: `录音中 · ${message.senderName}`, action: "播放录音" };
  if (context === "广播中") return { title: "广播信号接入", subtitle: `广播中 · ${message.senderName}`, action: "播放广播" };
  return { title: "声音事件", subtitle: `${context ?? "剧情中"} · ${message.senderName}`, action: "播放声音" };
}

export function playtestMessageContextLabel(
  senderType: PlaytestView["messages"][number]["senderType"],
  contextLabel: string | null | undefined,
): string | null {
  if (senderType === "USER") return "你";
  const label = contextLabel?.trim();
  return label && label !== "同幕中" ? label : null;
}

function PlaytestMessage({
  run,
  message,
  onMark,
}: {
  run: PlaytestView;
  message: PlaytestView["messages"][number];
  onMark: (messageId: string) => void;
}): React.ReactNode {
  const user = message.senderType === "USER";
  const narrator = message.senderType === "NARRATOR" || message.senderType === "SYSTEM";
  if (narrator) {
    return <article className="playtest-narrator"><span>旁白</span><p>{message.content}</p></article>;
  }
  if (message.presentation === "SOUND_EVENT") {
    const copy = soundEventCopy(message);
    return (
      <article className="playtest-sound-event">
        <header>
          <span className="playtest-sound-event-icon" aria-hidden="true">☎</span>
          <div><strong>{copy.title}</strong><small>{copy.subtitle}</small></div>
        </header>
        <p>{message.content}</p>
        {message.canPlay ? <MessageAudioButton runId={run.id} message={message} label={copy.action} /> : null}
        <button type="button" className="playtest-mark-message" onClick={() => onMark(message.id)}>标记这段</button>
      </article>
    );
  }
  const whisper = message.visibility === "WHISPER";
  const contextLabel = playtestMessageContextLabel(message.senderType, message.contextLabel);
  const portraitKey = playtestMessagePortraitKey(run.playableCharacter, run.otherCharacters, message);
  return (
    <article className={`playtest-message ${user ? "user" : "ai"} ${whisper ? "whisper" : ""}`}>
      <CharacterPortrait name={message.senderName} portraitKey={portraitKey} className="playtest-avatar" />
      <div>
        <header>
          <strong>{message.senderName}</strong>
          {contextLabel ? <span>{contextLabel}</span> : null}
        </header>
        {!whisper && message.body?.actionText ? <p className="playtest-character-action" aria-label="角色动作">{message.body.actionText}</p> : null}
        {whisper ? (
          <div className="playtest-whisper">
            <p>{message.publicCaption ?? `${message.senderName}说了一句悄悄话`}</p>
            <MessageAudioButton runId={run.id} message={message} />
          </div>
        ) : (
          <div className="playtest-bubble">
            <p>{message.content}</p>
            {!user && message.canPlay ? <MessageAudioButton runId={run.id} message={message} /> : null}
          </div>
        )}
        {!user ? <button type="button" className="playtest-mark-message" onClick={() => onMark(message.id)}>标记这句</button> : null}
      </div>
    </article>
  );
}

function PendingPlaytestMessage({
  run,
  reply,
  busy,
  onRetry,
}: {
  run: PlaytestView;
  reply: PendingPlaytestReply;
  busy: boolean;
  onRetry: () => void;
}): React.ReactNode {
  const failed = reply.status === "FAILED";
  return (
    <article className={`playtest-message user playtest-pending-message ${failed ? "failed" : "sending"}`} aria-live="polite">
      <CharacterPortrait name={run.playableCharacter.name} portraitKey={run.playableCharacter.portraitKey} className="playtest-avatar" />
      <div>
        <header><strong>{run.playableCharacter.name}</strong><span>你</span></header>
        <div className="playtest-bubble"><p>{reply.content}</p></div>
        <div className="playtest-pending-status">
          <span>{failed ? "发送失败" : "发送中…"}</span>
          {failed ? <button type="button" onClick={onRetry} disabled={busy}>重新发送</button> : null}
        </div>
      </div>
    </article>
  );
}

function FeedbackPanel({
  run,
  selectedMessageId,
  busy,
  onClose,
  onSubmit,
}: {
  run: PlaytestView;
  selectedMessageId: string | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: CreateSimulationIssueInput) => Promise<boolean>;
}): React.ReactNode {
  const [type, setType] = useState<SimulationIssueType>("DIALOGUE_QUALITY");
  const [severity, setSeverity] = useState<SimulationIssueSeverity>("WARNING");
  const [note, setNote] = useState("");
  const current = ISSUE_TAGS.find((item) => item.type === type)!;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (await onSubmit({ messageId: selectedMessageId, type, severity, note })) {
      setNote("");
      onClose();
    }
  }

  return (
    <aside className="playtest-feedback-panel" aria-label="标记试玩问题">
      <header><div><strong>这一刻哪里不对？</strong><span>问题会绑定到本次试玩，供规则测试复盘</span></div><button type="button" onClick={onClose} aria-label="关闭问题标记">×</button></header>
      {selectedMessageId ? <div className="playtest-feedback-context">已关联到当前这句角色消息</div> : <div className="playtest-feedback-context">记录整体体验问题</div>}
      <div className="playtest-issue-tags">
        {ISSUE_TAGS.map((item) => <button type="button" className={type === item.type ? "active" : ""} onClick={() => setType(item.type)} key={item.type}>{item.label}</button>)}
      </div>
      <p>{current.hint}</p>
      <form onSubmit={submit}>
        <label><span>具体发生了什么</span><textarea rows={4} maxLength={2000} value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：顾寻这一句太像在解释设定，不像一个正被怀疑的人。" required /></label>
        <label><span>影响程度</span><select value={severity} onChange={(event) => setSeverity(event.target.value as SimulationIssueSeverity)}>{Object.entries(SEVERITY_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
        <button className="primary-button compact" disabled={busy || !note.trim()}>{busy ? "正在记录…" : "记录这个问题"}</button>
      </form>
      <small>本次已记录 {run.issueCount} 个问题</small>
    </aside>
  );
}

export function ExperienceReviewPanel({
  run,
  busy,
  elapsedSeconds,
  onSubmit,
  onReview,
  onNew,
}: {
  run: PlaytestView;
  busy: boolean;
  elapsedSeconds: number;
  onSubmit: (input: SavePlaytestExperienceReviewInput) => Promise<boolean>;
  onReview: () => void;
  onNew: () => void;
}): React.ReactNode {
  const [ratings, setRatings] = useState<Record<ExperienceReviewKey, PlaytestExperienceRating>>(
    () => Object.fromEntries(
      EXPERIENCE_REVIEW_ITEMS.map((item) => [item.key, "NOT_ASSESSED"]),
    ) as Record<ExperienceReviewKey, PlaytestExperienceRating>,
  );
  const [note, setNote] = useState("");
  const saved = run.experienceReview;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await onSubmit({ ...ratings, note });
  }

  if (saved) {
    const verdictLabel = saved.overallVerdict === "PASS"
      ? "普通玩家体验通过"
      : saved.overallVerdict === "FAIL"
        ? "普通玩家体验不通过"
        : "普通玩家体验未完整评审";
    return (
      <section className={`playtest-experience-result ${saved.overallVerdict.toLowerCase()}`}>
        <span>普通玩家体验结论</span>
        <h2>{verdictLabel}</h2>
        <div className="playtest-experience-result-grid">
          {EXPERIENCE_REVIEW_ITEMS.map((item) => (
            <div key={item.key}><span>{item.label}</span><strong>{EXPERIENCE_RATING_LABELS[saved[item.key]]}</strong></div>
          ))}
        </div>
        <p>{saved.note}</p>
        <small>{saved.reviewedBy?.displayName ?? "管理员"} · {new Date(saved.reviewedAt).toLocaleString()}</small>
        <div><button type="button" className="primary-button compact" onClick={onReview}>进入技术复盘</button><button type="button" className="secondary-button" onClick={onNew}>再选一个角色</button></div>
      </section>
    );
  }

  return (
    <section className="playtest-experience-review">
      <header>
        <div><span>{run.status === "COMPLETED" ? "故事已收束" : run.status === "STOPPED" ? "试玩已结束" : "本次体验中断"}</span><h2>体验评审（可选）</h2><p>你以 {run.playableCharacter.name} 的身份体验了 {elapsedLabel(elapsedSeconds)}，推进 {run.turnSequence} 个回合。愿意的话，可以留下真实感受。</p></div>
        <div><button type="button" className="secondary-button" disabled={busy} onClick={onNew}>跳过评审，重新开始</button><p>旧局记录保留，仍为未评审；未保存的填写内容不会提交。</p></div>
      </header>
      <form onSubmit={submit}>
        <div className="playtest-experience-checks">
          {EXPERIENCE_REVIEW_ITEMS.map((item) => (
            <label key={item.key}>
              <span><strong>{item.label}</strong><small>{item.question}</small></span>
              <select value={ratings[item.key]} onChange={(event) => setRatings({ ...ratings, [item.key]: event.target.value as PlaytestExperienceRating })}>
                {Object.entries(EXPERIENCE_RATING_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
              </select>
            </label>
          ))}
        </div>
        <label className="playtest-experience-note"><span>像普通玩家一样说出总体感受</span><textarea rows={4} maxLength={2000} value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：我能看懂发生了什么，但角色一直在重复我的安排，没有让我感觉他们是有自己想法的人。" required /></label>
        <footer><small>只要有一项不通过，本次普通玩家体验就不通过；未评审项目会使结论保持未完整。</small><button className="primary-button compact" disabled={busy || !note.trim()}>{busy ? "正在保存…" : "保存体验结论"}</button></footer>
      </form>
    </section>
  );
}

function RoleBrief({ run }: { run: PlaytestView }): React.ReactNode {
  return (
    <aside className="playtest-role-brief">
      <div className="playtest-role-card">
        <CharacterPortrait name={run.playableCharacter.name} portraitKey={run.playableCharacter.portraitKey} className="playtest-profile-avatar" />
        <div><small>你是</small><strong>{run.playableCharacter.name}</strong><p>{run.playableCharacter.publicDescription}</p></div>
      </div>
      <section className="playtest-entry-situation"><span>{run.guidance?.primaryThread ?? "你现在面对的情况"}</span><p>{run.guidance?.knownFact ?? (run.playableCharacter.entrySituation || "跟随眼前发生的事件，以这个角色的立场作出反应。")}</p></section>
      <section><span>你记得的事</span><p>{run.playableCharacter.privateProfile || "这个角色暂时没有额外的私人背景。"}</p></section>
      <section><span>你放不下的事</span><p>{run.playableCharacter.personalObjective || "跟随现场线索，自主决定你相信谁。"}</p></section>
      <section className="playtest-cast"><span>同幕角色</span>{run.otherCharacters.map((character) => <div key={character.id}><CharacterPortrait name={character.name} portraitKey={character.portraitKey} className="playtest-cast-avatar" /><p><strong>{character.name}</strong><small>{character.publicDescription}</small></p></div>)}</section>
      <div className="playtest-role-warning">这些资料只属于你。不要假设其他角色也知道。</div>
    </aside>
  );
}

function PlaytestStage({
  run,
  busy,
  now,
  onSend,
  onContinue,
  onResume,
  onStop,
  onReview,
  onNew,
  onIssue,
  onExperienceReview,
}: {
  run: PlaytestView;
  busy: boolean;
  now: number;
  onSend: (content: string, clientMessageId: string) => Promise<boolean>;
  onContinue: () => Promise<boolean>;
  onResume: () => Promise<boolean>;
  onStop: () => void;
  onReview: () => void;
  onNew: () => void;
  onIssue: (input: CreateSimulationIssueInput) => Promise<boolean>;
  onExperienceReview: (input: SavePlaytestExperienceReviewInput) => Promise<boolean>;
}): React.ReactNode {
  const [content, setContent] = useState("");
  const [pendingReply, setPendingReply] = useState<PendingPlaytestReply | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const stopTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [lastTypedAt, setLastTypedAt] = useState(0);
  const [attemptedContinuation, setAttemptedContinuation] = useState("");
  const logRef = useRef<HTMLDivElement | null>(null);
  const terminal = ["COMPLETED", "STOPPED", "FAILED"].includes(run.status);
  const presentCharacters = run.otherCharacters.filter((character) => !run.presentCharacterIds || run.presentCharacterIds.includes(character.id));
  const liveElapsed = terminal
    ? run.elapsedSeconds
    : Math.max(run.elapsedSeconds, Math.round((now - new Date(run.startedAt).getTime()) / 1_000));

  const visiblePendingReply = pendingReply && !playtestHasClientMessage(run.messages, pendingReply.clientMessageId)
    ? pendingReply
    : null;
  const canSubmitMessage = playtestCanSubmitMessage({
    content,
    canSend: run.canSend,
    busy,
    hasPendingReply: Boolean(pendingReply),
  });

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [run.messages.length, busy, visiblePendingReply]);

  useEffect(() => {
    const availableAt = run.continuation.availableAt;
    if (terminal || busy || pendingReply || content.trim() || !availableAt) return;
    const lastMessageId = run.messages.at(-1)?.id ?? "opening";
    const attemptKey = `${run.id}:${lastMessageId}:${run.continuation.used}`;
    if (attemptedContinuation === attemptKey) return;
    const dueAt = Math.max(Date.parse(availableAt), lastTypedAt ? lastTypedAt + 8_000 : 0);
    const timer = window.setTimeout(() => {
      setAttemptedContinuation(attemptKey);
      void onContinue();
    }, Math.max(0, dueAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [attemptedContinuation, busy, content, lastTypedAt, onContinue, pendingReply, run.continuation.availableAt, run.continuation.used, run.id, run.messages, terminal]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSubmitMessage) return;
    const value = content.trim();
    const clientMessageId = crypto.randomUUID();
    setPendingReply({ content: value, clientMessageId, status: "SENDING" });
    setContent("");
    if (await onSend(value, clientMessageId)) {
      setPendingReply(null);
    } else {
      setPendingReply({ content: value, clientMessageId, status: "FAILED" });
    }
  }

  async function retryPendingReply(): Promise<void> {
    if (!pendingReply || pendingReply.status !== "FAILED" || busy) return;
    const retry = { ...pendingReply, status: "SENDING" as const };
    setPendingReply(retry);
    if (await onSend(retry.content, retry.clientMessageId)) {
      setPendingReply(null);
    } else {
      setPendingReply({ ...retry, status: "FAILED" });
    }
  }

  function mark(messageId: string | null): void {
    setFeedbackMessageId(messageId);
    setFeedbackOpen(true);
  }

  function confirmExit(button: HTMLButtonElement): void {
    stopTriggerRef.current = button;
    setConfirmStop(true);
  }

  function cancelExit(): void {
    setConfirmStop(false);
    stopTriggerRef.current?.focus();
  }

  return (
    <div className="playtest-stage">
      <header className="playtest-stage-header">
        <div><span className="playtest-live-dot" /><span>内部试玩 · 玩家视角</span><strong>{run.story.title}</strong><small>R{run.story.draftRevision} · {run.engineVersion === "AGENT_V2" ? "智能体版" : "历史规则记录 · 只读"} · 导演：{run.directorRuntime.modelName}{run.directorRuntime.roleModelProfile === "DOUBAO_CHARACTER" ? ` · 角色：${run.directorRuntime.roleModelName}（独立测试）` : ""}</small></div>
        <div className="playtest-session-meta"><span>{run.directorRuntime.externalCallCount === 0 ? "尚无真实模型调用" : `真实 AI ${run.directorRuntime.externalCallCount} 次 · 成功 ${run.directorRuntime.successCount} · 回退 ${run.directorRuntime.fallbackCount} · 错误 ${run.directorRuntime.errorCount}`}</span><strong>{elapsedLabel(liveElapsed)}</strong><button type="button" onClick={() => mark(null)}>＋ 标记问题 <i>{run.issueCount}</i></button></div>
      </header>
      <div className="playtest-stage-grid">
        <RoleBrief run={run} />
        <main className="playtest-chat">
          <header><div><span>{run.currentScene.title}</span><strong>{presentCharacters.length ? `与 ${presentCharacters.map((item) => item.name).join("、")} 同幕` : "此刻只有你在这里"}</strong></div><button type="button" className="playtest-exit-button" onClick={(event) => confirmExit(event.currentTarget)} disabled={terminal}>退出本局</button></header>
          <div className="playtest-chat-log" ref={logRef} aria-live="polite">
            {run.messages.map((message) => <PlaytestMessage run={run} message={message} onMark={mark} key={message.id} />)}
            {visiblePendingReply && run.status !== "PAUSED" ? <PendingPlaytestMessage run={run} reply={visiblePendingReply} busy={busy} onRetry={() => void retryPendingReply()} /> : null}
            {busy ? <div className="playtest-thinking"><span /><span /><span /><p>导演与角色正在完成这次回应……</p></div> : null}
          </div>
          {terminal ? (
            <ExperienceReviewPanel run={run} busy={busy} elapsedSeconds={liveElapsed} onSubmit={onExperienceReview} onReview={onReview} onNew={onNew} />
          ) : run.status === "CREATED" && run.opening ? (
            <section className="playtest-composer" role="status"><strong>{run.opening.phase === "ROLE" ? "角色正在准备第一句回应…" : "正在准备这次相遇…"}</strong>
              <p>已等待 {Math.max(0, Math.floor((now - new Date(run.opening.startedAt).getTime()) / 1_000))} 秒。可以先看看你的身份和记忆，回应准备好后会自动出现。</p>
              <small>刷新会查询同一次生成，不会重新调用模型。你也可以结束并保留这次记录。</small>
            </section>
          ) : run.status === "PAUSED" ? (
            <section className="playtest-composer" role="status"><strong>现场已暂停，故事没有结束</strong><p>{run.pauseReason}</p>
              <div className="playtest-paused-actions">
                <button type="button" className="secondary-button" disabled={busy} onClick={() => { void onResume().then((ok) => {
                  if (ok) { setContent(pendingReply?.content ?? content); setPendingReply(null); }
                }); }}>{run.turnSequence === 0 && run.messages.every((message) => message.senderType !== "AI_CHARACTER") ? "重新生成开场" : "恢复现场，暂不发送"}</button>
                <button type="button" className="playtest-exit-button" onClick={(event) => confirmExit(event.currentTarget)}>退出本局</button>
              </div>
            </section>
          ) : (
            <form className="playtest-composer" onSubmit={submit}>
              {run.guidance || (run.turnSequence === 0 && run.playableCharacter.entrySuggestions.length) ? <div className="playtest-entry-suggestions"><span>{run.guidance?.openQuestion ?? "这一刻，你可以——"}</span><div>{(run.guidance?.suggestions ?? run.playableCharacter.entrySuggestions).map((suggestion) => <button type="button" key={suggestion} onClick={() => { setContent(suggestion); setLastTypedAt(Date.now()); }} disabled={!run.canSend || busy}>{suggestion}</button>)}</div><small>只是行动灵感，也可以完全按自己的想法回应。</small></div> : null}
              <textarea
                value={content}
                onChange={(event) => { setContent(event.target.value); setLastTypedAt(Date.now()); }}
                onKeyDown={(event) => {
                  if (!playtestEnterSubmitsMessage({
                    key: event.key,
                    shiftKey: event.shiftKey,
                    isComposing: event.nativeEvent.isComposing,
                  }) || !canSubmitMessage) return;
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }}
                rows={3}
                maxLength={4000}
                placeholder={`直接回应刚才的人，或者描述${run.playableCharacter.name}想做的事……`}
                disabled={!run.canSend || busy || Boolean(pendingReply)}
              />
              <div className="playtest-composer-footer"><div><small>Enter 发送，Shift+Enter 换行。先写真实第一反应，不要为了触发状态安排整套测试步骤。</small>{run.turnSequence > 0 || !run.playableCharacter.entrySuggestions.length ? <details><summary>一时不知道怎么回应？</summary><p>可以先追问刚才的人，或者只描述这一刻最想做的一件事。</p></details> : null}</div><button className="primary-button compact" aria-keyshortcuts="Enter" disabled={!canSubmitMessage}>{busy ? "等待回应…" : pendingReply ? "请先重新发送" : "发送"}</button></div>
            </form>
          )}
        </main>
        {feedbackOpen ? <FeedbackPanel run={run} selectedMessageId={feedbackMessageId} busy={busy} onClose={() => setFeedbackOpen(false)} onSubmit={onIssue} /> : <aside className="playtest-observer-note"><span>◌</span><strong>保持普通玩家心态</strong><p>不看状态机，不为测试而设计输入。先感受是否看得懂、像真人、想继续；出戏时再标记问题。</p><button type="button" onClick={() => mark(null)}>记录体验问题</button></aside>}
      </div>
      {confirmStop ? <div className="dialog-backdrop"><section className="dialog playtest-stop-dialog" role="dialog" aria-modal="true" aria-labelledby="playtest-stop-title" onKeyDown={(event) => { if (event.key === "Escape") { event.preventDefault(); cancelExit(); } }}><header><div><p className="eyebrow">END PLAYTEST</p><h2 id="playtest-stop-title">确定退出本局？</h2></div></header><p>当前聊天、回合和问题都会保留。退出不会触发剧情结局，之后可填写或跳过体验评审。</p><footer><button type="button" className="secondary-button" autoFocus onClick={cancelExit}>继续体验</button><button type="button" className="danger-button" onClick={() => { setConfirmStop(false); onStop(); }}>退出并保留记录</button></footer></section></div> : null}
    </div>
  );
}

export function PendingPlaytestEntry({ busy, creating = false, queryMessage, onQuery }: {
  busy: boolean;
  creating?: boolean;
  queryMessage: string;
  onQuery: () => void;
}): React.ReactNode {
  return <section className="playtest-entry-shell">
    <div className="playtest-entry-empty" aria-live="polite">
      <h1>{creating ? "正在进入故事" : "上次进入结果待确认"}</h1>
      <p>{creating ? "正在提交本次进入请求，请稍候。" : "先确认上次请求的结果，再开始新的试玩。"}</p>
      {queryMessage ? <p role="status">{queryMessage}</p> : null}
      <button type="button" className="primary-button" onClick={onQuery} disabled={busy}>
        {creating ? "正在进入故事…" : busy ? "正在查询进入结果…" : "查询进入结果"}
      </button>
      <p>查询不会重新创建故事或调用模型。</p>
    </div>
  </section>;
}

export function PlaytestWorkbench({
  initialStoryId = "",
  onReview,
}: {
  initialStoryId?: string;
  onReview: (runId: string) => void;
}): React.ReactNode {
  const [catalog, setCatalog] = useState<PlaytestCatalogItem[]>([]);
  const [selectedStoryId, setSelectedStoryId] = useState(initialStoryId);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [setupStage, setSetupStage] = useState<"prelude" | "roles">("prelude");
  const [roleModelProfile, setRoleModelProfile] = useState<"CONFIGURED" | "DOUBAO_CHARACTER">("CONFIGURED");
  const [run, setRun] = useState<PlaytestView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [now, setNow] = useState(Date.now());
  const entryRequest = useRef(sessionStorage.getItem("actale.playtest.request"));
  const entryInFlight = useRef(false);
  const [entryCreating, setEntryCreating] = useState(false);
  const [entryFailure, setEntryFailure] = useState(() => entryRequest.current ? sessionStorage.getItem("actale.playtest.request-error") ?? "" : "");
  const [entryQueryMessage, setEntryQueryMessage] = useState("");

  function saveEntryFailure(message: string): void {
    setEntryFailure(message);
    if (message) sessionStorage.setItem("actale.playtest.request-error", message);
    else sessionStorage.removeItem("actale.playtest.request-error");
  }

  function clearEntryRequest(): void {
    entryRequest.current = null;
    sessionStorage.removeItem("actale.playtest.request");
    saveEntryFailure("");
    setEntryQueryMessage("");
  }

  async function recoverEntry(): Promise<void> {
    if (!entryRequest.current || entryInFlight.current) return;
    entryInFlight.current = true;
    setBusy(true);
    setEntryQueryMessage("");
    try {
      setRun(await api.playtestByRequest(entryRequest.current));
      saveEntryFailure("");
    } catch (caught) {
      setEntryQueryMessage(playtestEntryQueryText(caught));
    } finally {
      entryInFlight.current = false;
      setBusy(false);
    }
  }

  useEffect(() => {
    if (entryRequest.current) void recoverEntry();
  }, []);

  useEffect(() => {
    if (!run?.opening || run.status !== "CREATED") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function poll(): Promise<void> {
      try {
        const value = await api.playtest(run!.id);
        if (!cancelled) { setRun(value); setError(""); }
      } catch { if (!cancelled) setError("暂时无法读取开场进度，正在查询原记录；不会重新调用模型。"); }
      if (!cancelled) timer = setTimeout(() => void poll(), 1_500);
    }
    timer = setTimeout(() => void poll(), 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [run?.id, run?.status]);

  useEffect(() => {
    api.playtestCatalog()
      .then((items) => {
        setCatalog(items);
        const storyId = initialStoryId && items.some((item) => item.id === initialStoryId)
          ? initialStoryId
          : items.find((item) => item.ready)?.id ?? items[0]?.id ?? "";
        setSelectedStoryId(storyId);
      })
      .catch((caught) => setError(errorText(caught, "无法读取内部试玩目录")))
      .finally(() => setLoading(false));
  }, [initialStoryId]);

  useEffect(() => {
    setSelectedRoleId("");
    setRoleModelProfile("CONFIGURED");
    setSetupStage("prelude");
  }, [catalog, selectedStoryId]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  async function action(
    work: () => Promise<PlaytestView>,
    apply: (value: PlaytestView) => void,
    message = "",
  ): Promise<boolean> {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const value = await work();
      apply(value);
      setSuccess(value.status === "PAUSED" ? "" : message);
      return true;
    } catch (caught) {
      setError(errorText(caught, "内部试玩操作失败"));
      if (run) {
        try { setRun(await api.playtest(run.id)); } catch { /* Keep the current view if the read also fails. */ }
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function start(): Promise<void> {
    if (busy || entryInFlight.current) return;
    if (entryRequest.current) { void recoverEntry(); return; }
    if (!selectedStoryId || !selectedRoleId) return;
    const requestId = crypto.randomUUID();
    entryInFlight.current = true;
    entryRequest.current = requestId;
    setEntryCreating(true);
    sessionStorage.setItem("actale.playtest.request", requestId);
    saveEntryFailure("");
    setEntryQueryMessage("");
    setError("");
    setSuccess("");
    setBusy(true);
    try {
      setRun(await api.createPlaytest(selectedStoryId, selectedRoleId, roleModelProfile, requestId));
    } catch (caught) {
      if (playtestEntryWasRejected(caught)) {
        clearEntryRequest();
        setError(playtestEntryFailureText(caught));
      } else {
        saveEntryFailure(playtestEntryFailureText(caught));
      }
    } finally {
      entryInFlight.current = false;
      setEntryCreating(false);
      setBusy(false);
    }
  }

  async function send(content: string, clientMessageId: string): Promise<boolean> {
    if (!run) return false;
    return action(
      () => api.sendPlaytestMessage(run.id, content, clientMessageId),
      setRun,
    );
  }

  const continueStory = useCallback(async (): Promise<boolean> => {
    if (!run) return false;
    return action(
      () => api.continuePlaytest(run.id, crypto.randomUUID()),
      setRun,
    );
  }, [run]);

  function stop(): void {
    if (!run) return;
    void action(() => api.stopPlaytest(run.id), setRun, "试玩记录已保留。");
  }

  async function issue(input: CreateSimulationIssueInput): Promise<boolean> {
    if (!run) return false;
    return action(
      () => api.createPlaytestIssue(run.id, input),
      setRun,
      "体验问题已记录，试玩可以继续。",
    );
  }

  async function experienceReview(input: SavePlaytestExperienceReviewInput): Promise<boolean> {
    if (!run) return false;
    return action(
      () => api.savePlaytestExperienceReview(run.id, input),
      setRun,
      "普通玩家体验结论已保存，现在可以进入技术复盘。",
    );
  }

  return (
    <div className="playtest-page">
      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {!run && entryFailure ? <div className="error-notice" role="alert"><span>!</span>{entryFailure}</div> : null}
      {success ? <div className="save-notice" role="status"><span>✓</span>{success}</div> : null}
      {loading ? <div className="playtest-loading"><span /><span /><span /><p>正在准备内部试玩环境…</p></div> : null}
      {!run && entryRequest.current ? <PendingPlaytestEntry busy={busy} creating={entryCreating} queryMessage={entryQueryMessage} onQuery={() => void recoverEntry()} /> : null}
      {!loading && !run && !entryRequest.current ? <RoleSetup catalog={catalog} stage={setupStage} setStage={setSetupStage} selectedStoryId={selectedStoryId} selectedRoleId={selectedRoleId} busy={busy} onStory={setSelectedStoryId} onRole={setSelectedRoleId} onStart={() => void start()} roleModelProfile={roleModelProfile} onRoleModelProfile={setRoleModelProfile} /> : null}
      {run ? <PlaytestStage run={run} busy={busy} now={now} onSend={send} onContinue={continueStory} onResume={() => action(() => api.resumePlaytest(run.id), setRun)} onStop={stop} onReview={() => onReview(run.id)} onNew={() => { clearEntryRequest(); setRun(null); setSelectedRoleId(""); setSetupStage("roles"); setSuccess(""); setError(""); }} onIssue={issue} onExperienceReview={experienceReview} /> : null}
    </div>
  );
}
